import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import axios from '../../api';
import TimeRangeInput from './TimeRangeInput';

const REMINDER_MINUTES_OPTIONS = [
  { value: 5, label: '5 分钟' },
  { value: 15, label: '15 分钟' },
  { value: 30, label: '30 分钟' },
  { value: 1440, label: '1 天' },
];
const REMINDER_CHANNELS_OPTIONS = [
  { value: 'email', label: '邮件' },
  { value: 'wecom', label: '企微' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const displayName = (row) => row?.real_name || row?.username || row?.email || (row?.id ? `#${row.id}` : '—');

/**
 * 新建/编辑排课弹窗，老师与参会人可自由选择（不绑定当前用户）
 * @param {boolean} open - 是否显示
 * @param {function} onClose - 关闭回调
 * @param {number|null} editingId - 编辑时的排课 ID，null 为新建
 * @param {function} onSuccess - 创建/更新成功回调
 */
/** 将日期范围展开为 YYYY-MM-DD 数组 */
const expandDateRange = (range) => {
  if (!range || !Array.isArray(range) || range.length < 2 || !range[0] || !range[1]) return [];
  const start = dayjs(range[0]).startOf('day');
  const end = dayjs(range[1]).startOf('day');
  if (start.isAfter(end)) return [];
  const list = [];
  for (let d = start; !d.isAfter(end); d = d.add(1, 'day')) list.push(d.format('YYYY-MM-DD'));
  return list;
};

/** 判断日期列表是否连续（可表示为一段范围） */
const isConsecutiveDates = (dates) => {
  if (!dates || dates.length === 0) return false;
  const sorted = [...dates].filter(Boolean).map((s) => s.slice(0, 10)).sort();
  const uniq = [...new Set(sorted)];
  if (uniq.length !== sorted.length) return false;
  const start = dayjs(uniq[0]);
  const end = dayjs(uniq[uniq.length - 1]);
  const expectedCount = end.diff(start, 'day') + 1;
  return expectedCount === uniq.length && uniq.every((s, i) => dayjs(s).diff(start, 'day') === i);
};

const ScheduleFormModal = ({ open, onClose, editingId, onSuccess }) => {
  const [form] = Form.useForm();
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [defaultSettings, setDefaultSettings] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [createMeetLoading, setCreateMeetLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [tRes, sRes, settingsRes] = await Promise.all([
        axios.get('/api/schedules/contacts/', { params: { role: 'teacher', page_size: 500 } }),
        axios.get('/api/schedules/contacts/', { params: { role: 'student', page_size: 500 } }),
        axios.get('/api/schedules/default-settings/'),
      ]);
      setTeachers(tRes.data.results || []);
      setStudents(sRes.data.results || []);
      setDefaultSettings(settingsRes.data || {});
    } catch {
      setTeachers([]);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!open) return;
    if (editingId) {
      axios
        .get(`/api/schedules/${editingId}/`)
        .then((res) => {
          const d = res.data;
          const hasDates = Array.isArray(d.class_dates) && d.class_dates.length > 0 && d.time_slot_start != null;
          const start = d.start_time ? dayjs(d.start_time) : null;
          const end = d.end_time ? dayjs(d.end_time) : null;
          const participantIds = (d.participants || []).map((p) => p.user_id);
          const timeStart = d.time_slot_start ? dayjs(d.time_slot_start, 'HH:mm:ss') : dayjs().hour(14).minute(0).second(0);
          const timeEnd = d.time_slot_end ? dayjs(d.time_slot_end, 'HH:mm:ss') : dayjs().hour(15).minute(0).second(0);
          const dates = d.class_dates || [];
          let classDateRange = null;
          let classDateMode = 'range';
          let classDatesList = [];
          if (hasDates && dates.length > 0) {
            if (isConsecutiveDates(dates)) {
              classDateMode = 'range';
              classDateRange = [dayjs(dates[0]), dayjs(dates[dates.length - 1])];
            } else {
              classDateMode = 'specific';
              classDatesList = [...dates].sort();
            }
          } else if (start && end) {
            classDateRange = [start.startOf('day'), end.startOf('day')];
          }
          form.setFieldsValue({
            course_name: d.course_name,
            time_slot_range: [timeStart, timeEnd],
            class_date_mode: classDateMode,
            class_date_range: classDateRange,
            class_dates: classDatesList,
            teacher_id: d.teacher_id,
            participant_ids: participantIds,
            reminder_lead_minutes: d.reminder_lead_minutes || [],
            reminder_channels: d.reminder_channels || [],
            wecom_contact_sender_userid: d.wecom_contact_sender_userid || '',
            meet_link: d.meet_link || '',
            frame_slug: d.frame_slug || '',
            status: d.status,
          });
        })
        .catch((e) => message.error(e.response?.data?.detail || '加载排课失败'));
    } else {
      form.resetFields();
      form.setFieldsValue({
        time_slot_range: [dayjs().hour(14).minute(0).second(0).millisecond(0), dayjs().hour(15).minute(0).second(0).millisecond(0)],
        class_date_mode: 'range',
        class_date_range: null,
        class_dates: [],
        reminder_lead_minutes: [],
        reminder_channels: [],
        wecom_contact_sender_userid: '',
        participant_ids: [],
        frame_slug: 'https://framevr.io/republic-of-arts',
        status: 'published',
      });
    }
  }, [open, editingId, form]);

  const onFormFinish = async (values) => {
    setSubmitLoading(true);
    try {
      const range = values.time_slot_range;
      const payload = {
        course_name: values.course_name,
        teacher_id: values.teacher_id,
        participant_ids: values.participant_ids || [],
        reminder_lead_minutes: values.reminder_lead_minutes || [],
        reminder_channels: values.reminder_channels || [],
        wecom_contact_sender_userid: values.wecom_contact_sender_userid || null,
        meet_link: values.meet_link || null,
        frame_slug: (() => {
          const frameValue = (values.frame_slug || '').trim();
          if (!frameValue) return null;
          const urlMatch = frameValue.match(/framevr\.io\/([^\/\?]+)/);
          if (urlMatch) return urlMatch[1];
          return frameValue;
        })(),
        status: values.status,
        time_slot_start: range?.[0] ? range[0].format('HH:mm:ss') : null,
        time_slot_end: range?.[1] ? range[1].format('HH:mm:ss') : null,
        class_dates: values.class_date_mode === 'range'
          ? (expandDateRange(values.class_date_range) || [])
          : (values.class_dates && values.class_dates.length ? [...values.class_dates].sort() : []),
        start_time: null,
        end_time: null,
      };
      let res;
      if (editingId) {
        res = await axios.put(`/api/schedules/${editingId}/`, payload);
        message.success('已更新');
      } else {
        res = await axios.post('/api/schedules/', payload);
        message.success('已创建');
      }
      onClose();
      if (onSuccess) onSuccess(res?.data);
      if (res?.data?.warnings?.length > 0) {
        const text = res.data.warnings.map((w) => `${w.user_display} 与「${w.other_course_name}」(${w.other_start}～${w.other_end}) 间隔较近（小于 10 分钟），请留意。`).join('\n');
        message.warning({ content: `课程已保存，但存在间隔较近的排课提醒：\n${text}`, duration: 8 });
      }
    } catch (e) {
      const err = e.response?.data;
      const msg = err?.non_field_errors?.[0] || err?.detail || (editingId ? '更新失败' : '创建失败');
      message.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title={editingId ? '编辑排课' : '新建排课'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFormFinish}>
        <Form.Item name="course_name" label="课程名" rules={[{ required: true }]}>
          <Input placeholder="课程名" />
        </Form.Item>
        <Form.Item name="class_date_mode" label="上课日期" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="range">按范围（开始日期～结束日期）</Radio>
            <Radio value="specific">按具体日期（选择哪些天上课）</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.class_date_mode !== curr?.class_date_mode || (curr?.class_date_mode === 'specific' && (prev?.class_dates?.length !== curr?.class_dates?.length || (prev?.class_dates?.join?.() !== curr?.class_dates?.join?.())))}>
          {({ getFieldValue, setFieldValue }) => {
            const mode = getFieldValue('class_date_mode');
            if (mode === 'range') {
              return (
                <Form.Item
                  name="class_date_range"
                  label="日期范围"
                  rules={[{ required: true, message: '请选择开始和结束日期' }, { type: 'array', len: 2, message: '请选择开始和结束日期' }]}
                >
                  <DatePicker.RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={['开始日期', '结束日期']} />
                </Form.Item>
              );
            }
            const classDates = getFieldValue('class_dates') || [];
            return (
              <Form.Item
                name="class_dates"
                label="具体日期"
                rules={[{ required: true, type: 'array', min: 1, message: '请至少添加一个上课日期' }]}
              >
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <Space wrap>
                      {classDates.map((d) => (
                        <Tag
                          key={d}
                          closable
                          onClose={() => setFieldValue('class_dates', classDates.filter((x) => x !== d))}
                        >
                          {d}
                        </Tag>
                      ))}
                      {classDates.length === 0 && <span style={{ color: '#999', fontSize: 12 }}>请下方添加日期</span>}
                    </Space>
                  </div>
                  <Space wrap>
                    <DatePicker
                      format="YYYY-MM-DD"
                      placeholder="选择日期（可多次选择添加）"
                      onChange={(_, dateStr) => {
                        if (dateStr && !classDates.includes(dateStr)) {
                          setFieldValue('class_dates', [...classDates, dateStr].sort());
                        }
                      }}
                    />
                    <Button type="link" size="small" onClick={() => setFieldValue('class_dates', [])}>
                      清空
                    </Button>
                  </Space>
                </div>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item name="time_slot_range" label="每天上课时间" rules={[{ required: true, type: 'array', len: 2, message: '请选择开始和结束时间' }]}>
          <TimeRangeInput />
        </Form.Item>
        <Form.Item name="teacher_id" label="老师" rules={[{ required: true }]}>
          <Select
            placeholder="请选择老师"
            allowClear
            loading={loading}
            options={teachers.map((t) => ({ value: t.id, label: displayName(t) }))}
          />
        </Form.Item>
        <Form.Item name="participant_ids" label="参会人（学生等）">
          <Select
            mode="multiple"
            placeholder="选择学生"
            allowClear
            loading={loading}
            options={students.map((s) => ({ value: s.id, label: displayName(s) }))}
          />
        </Form.Item>
        <Form.Item name="reminder_lead_minutes" label="提醒提前">
          <Checkbox.Group options={REMINDER_MINUTES_OPTIONS} />
        </Form.Item>
        <Form.Item name="reminder_channels" label="提醒渠道">
          <Checkbox.Group options={REMINDER_CHANNELS_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="wecom_contact_sender_userid"
          label="企微发送者UserID"
          tooltip="指定此课程企微通知的发送者（必须是已添加该客户的企微成员UserID），留空则使用默认配置或自动获取客户的跟进人"
        >
          <Input placeholder={defaultSettings.wecom_contact_sender_userid || '留空使用默认配置'} allowClear />
        </Form.Item>
        <Form.Item label="Meet 链接或会议 ID" extra="请填写真实创建的 Google Meet 链接；留空则一键启动中不会打开 Meet。可点击「由系统创建」调用 Google 日历自动生成可用会议链接。">
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="meet_link" noStyle>
              <Input placeholder="留空可稍后在详情页点击「创建 Meet 链接」" />
            </Form.Item>
            {editingId && (
              <Button
                type="default"
                loading={createMeetLoading}
                onClick={async () => {
                  setCreateMeetLoading(true);
                  try {
                    const res = await axios.post(`/api/schedules/${editingId}/create-meet/`);
                    if (res.data?.meet_link) {
                      form.setFieldValue('meet_link', res.data.meet_link);
                      message.success('Meet 会议链接已创建并填入');
                    }
                  } catch (e) {
                    message.error(e.response?.data?.detail || '创建失败');
                  } finally {
                    setCreateMeetLoading(false);
                  }
                }}
              >
                由系统创建
              </Button>
            )}
          </Space.Compact>
        </Form.Item>
        <Form.Item name="frame_slug" label="FrameVR 场景" extra="默认：https://framevr.io/republic-of-arts">
          <Input placeholder="https://framevr.io/republic-of-arts" />
        </Form.Item>
        <Form.Item name="status" label="状态" initialValue="published">
          <Select options={[{ value: 'draft', label: '草稿' }, { value: 'published', label: '已发布' }, { value: 'cancelled', label: '已取消' }]} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitLoading}>
              {editingId ? '保存' : '创建'}
            </Button>
            <Button onClick={onClose}>取消</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { ScheduleFormModal };
export default ScheduleFormModal;
